import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Banknote,
    TrendingUp,
    RefreshCw,
    Search,
    ExternalLink,
    Layers,
} from 'lucide-react';
import usageApi, { GlobalUsageSummary } from '../lib/api/usage';

const WINDOW_OPTIONS = [
    { label: '7d', value: 7 },
    { label: '30d', value: 30 },
    { label: '90d', value: 90 },
];

const SERVICE_LABELS: Record<string, string> = {
    'ai-chat': 'AI Chat',
    'document-analysis': 'Document Analysis',
    storage: 'Storage',
    email: 'Email',
    payment: 'Stripe Fees',
};

function formatService(service: string): string {
    return SERVICE_LABELS[service] || service.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatCost(value: number): string {
    if (value >= 100) return `$${value.toFixed(2)}`;
    if (value >= 1) return `$${value.toFixed(3)}`;
    return `$${value.toFixed(4)}`;
}

export const CostsPage: React.FC = () => {
    const navigate = useNavigate();
    const [summary, setSummary] = useState<GlobalUsageSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [windowDays, setWindowDays] = useState<number>(30);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchData = async (isRefresh = false) => {
        try {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);
            const data = await usageApi.getSummary(windowDays);
            setSummary(data);
        } catch (e) {
            console.error('Failed to fetch cost summary', e);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowDays]);

    const totalCost = useMemo(
        () => summary?.serviceTotals.reduce((s, item) => s + item.total, 0) || 0,
        [summary]
    );

    const peakDay = useMemo(() => {
        if (!summary || summary.dailyBreakdown.length === 0) return null;
        return summary.dailyBreakdown.reduce((max, d) => (d.total > max.total ? d : max));
    }, [summary]);

    const maxDailyTotal = useMemo(
        () => summary?.dailyBreakdown.reduce((m, d) => Math.max(m, d.total), 0) || 0,
        [summary]
    );

    const filteredUsers = useMemo(() => {
        if (!summary) return [];
        const q = searchQuery.toLowerCase();
        return summary.topUsers.filter(u =>
            !q ||
            u.userId.toLowerCase().includes(q) ||
            u.user.name?.toLowerCase().includes(q) ||
            u.user.email?.toLowerCase().includes(q)
        );
    }, [summary, searchQuery]);

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
                        <Banknote className="text-emerald-600" />
                        Service Costs
                    </h1>
                    <p className="text-neutral-500 text-sm">
                        Variable per-user expenses across AI, document analysis, storage, email, and payment processing.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 p-1 bg-white border border-neutral-200 rounded-lg">
                        {WINDOW_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => setWindowDays(opt.value)}
                                className={
                                    'px-3 py-1 text-xs font-semibold rounded-md transition-colors ' +
                                    (windowDays === opt.value
                                        ? 'bg-emerald-50 text-emerald-700'
                                        : 'text-neutral-500 hover:text-neutral-900')
                                }
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => fetchData(true)}
                        disabled={refreshing}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-neutral-200 rounded-lg text-xs font-medium text-neutral-600 hover:bg-neutral-50 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* Global stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">
                        Total Cost ({windowDays}d)
                    </div>
                    <div className="text-2xl font-bold text-emerald-600">
                        {loading ? '...' : formatCost(totalCost)}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400">
                        Sum of all logged service usage
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">
                        Services Active
                    </div>
                    <div className="text-2xl font-bold text-neutral-900">
                        {loading ? '...' : summary?.serviceTotals.length || 0}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400">
                        Distinct cost-tracked services
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">
                        Peak Day
                    </div>
                    <div className="text-2xl font-bold text-neutral-900">
                        {loading || !peakDay ? '...' : formatCost(peakDay.total)}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400">
                        {peakDay ? peakDay.date : 'No data yet'}
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">
                        Top Users
                    </div>
                    <div className="text-2xl font-bold text-neutral-900">
                        {loading ? '...' : summary?.topUsers.length || 0}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400 flex items-center gap-1">
                        <TrendingUp size={10} className="text-emerald-500" />
                        Highest cost contributors
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Top users table */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-neutral-100 bg-neutral-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <h2 className="text-sm font-semibold text-neutral-900">
                            Top Users by Cost ({windowDays}d)
                        </h2>
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" size={14} />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                placeholder="Search by name or email..."
                                className="pl-8 pr-3 py-1.5 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 w-full sm:w-64"
                            />
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase font-bold border-b border-neutral-100">
                                <tr>
                                    <th className="px-5 py-3">Customer</th>
                                    <th className="px-5 py-3">Share</th>
                                    <th className="px-5 py-3 text-right">Cost</th>
                                    <th className="px-5 py-3 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan={4} className="px-5 py-10 text-center text-neutral-400 text-xs italic">
                                            Loading top users...
                                        </td>
                                    </tr>
                                ) : filteredUsers.length > 0 ? (
                                    filteredUsers.map(item => {
                                        const share = totalCost > 0 ? (item.amount / totalCost) * 100 : 0;
                                        return (
                                            <tr key={item.userId} className="hover:bg-neutral-50/50 transition-colors group">
                                                <td className="px-5 py-4">
                                                    <div className="flex flex-col">
                                                        <span className="font-semibold text-neutral-900">{item.user.name || 'Unnamed'}</span>
                                                        <span className="text-[10px] text-neutral-500">{item.user.email}</span>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-24 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                                                            <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, share)}%` }} />
                                                        </div>
                                                        <span className="text-[10px] font-semibold text-neutral-500">{share.toFixed(1)}%</span>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4 text-right font-medium text-emerald-600">
                                                    {formatCost(item.amount)}
                                                </td>
                                                <td className="px-5 py-4 text-right">
                                                    <button
                                                        onClick={() => navigate(`/customers/${item.userId}`)}
                                                        className="p-1.5 rounded-lg border border-neutral-100 bg-white text-neutral-400 hover:text-emerald-600 hover:border-emerald-100 transition-all shadow-sm"
                                                        title="View Customer Details"
                                                    >
                                                        <ExternalLink size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td className="px-5 py-10 text-center text-neutral-400 text-xs italic" colSpan={4}>
                                            {searchQuery ? 'No users matching your search.' : 'No service usage recorded yet.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Right column */}
                <div className="space-y-6">
                    {/* Service breakdown */}
                    <div className="bg-white rounded-xl border border-neutral-200 p-5 shadow-sm">
                        <h3 className="text-sm font-semibold text-neutral-900 flex items-center gap-2 mb-4">
                            <Layers size={14} className="text-emerald-500" />
                            By Service
                        </h3>
                        {loading ? (
                            <div className="text-xs text-neutral-400 italic">Loading...</div>
                        ) : !summary || summary.serviceTotals.length === 0 ? (
                            <div className="text-xs text-neutral-400 italic">No service usage recorded.</div>
                        ) : (
                            <div className="space-y-3">
                                {[...summary.serviceTotals]
                                    .sort((a, b) => b.total - a.total)
                                    .map(s => {
                                        const share = totalCost > 0 ? (s.total / totalCost) * 100 : 0;
                                        return (
                                            <div key={s.service} className="space-y-1">
                                                <div className="flex justify-between text-xs">
                                                    <span className="text-neutral-700 font-medium">{formatService(s.service)}</span>
                                                    <span className="font-semibold text-neutral-900">{formatCost(s.total)}</span>
                                                </div>
                                                <div className="w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                                                    <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, share)}%` }} />
                                                </div>
                                                <div className="text-[10px] text-neutral-400">{share.toFixed(1)}% of total</div>
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </div>

                    {/* Daily sparkline */}
                    <div className="bg-white rounded-xl border border-neutral-200 p-5 shadow-sm">
                        <h3 className="text-sm font-semibold text-neutral-900 mb-4">Daily Spend</h3>
                        {loading ? (
                            <div className="text-xs text-neutral-400 italic">Loading...</div>
                        ) : !summary || summary.dailyBreakdown.length === 0 ? (
                            <div className="text-xs text-neutral-400 italic">No data in this window.</div>
                        ) : (
                            <div className="flex items-end gap-1 h-24">
                                {summary.dailyBreakdown.map(d => {
                                    const height = maxDailyTotal > 0 ? (d.total / maxDailyTotal) * 100 : 0;
                                    return (
                                        <div
                                            key={d.date}
                                            className="flex-1 bg-emerald-100 hover:bg-emerald-200 transition-colors rounded-sm relative group"
                                            style={{ height: `${Math.max(2, height)}%` }}
                                            title={`${d.date}: ${formatCost(d.total)}`}
                                        >
                                            <div className="absolute inset-x-0 bottom-full mb-1 hidden group-hover:block text-[10px] font-semibold text-neutral-700 text-center whitespace-nowrap">
                                                {formatCost(d.total)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
